import React, { useState, useEffect } from 'react';
import { Video, Loader2, Upload } from 'lucide-react';
import { TimelineItemLabel } from './timeline-item-label';
import { findMediaBySrc } from '../../../../../utils/general/indexdb';

interface VideoItemContentProps {
  label?: string;
  data?: {
    thumbnailUrl?: string;
    thumbnails?: string[];
    thumbnailTimestamps?: number[];
    duration?: number;
    fps?: number;
    resolution?: string;
    codec?: string;
    originalUrl?: string;
    src?: string; // Video source URL or File
    width?: number;
    height?: number;
    // Content timing properties
    startFromVideo?: number; // Start time in the source video (in seconds)
    endFromVideo?: number; // End time in the source video (in seconds)
    isLoadingThumbnails?: boolean; // Added from enhanced data
    thumbnailError?: string; // Added from enhanced data
  };
  itemWidth: number;
  itemHeight: number;
  start: number;
  end: number;
  isHovering?: boolean;
  onThumbnailDisplayChange?: (isShowingThumbnails: boolean) => void; // Callback to notify parent when thumbnails are displayed
}

export const VideoItemContent: React.FC<VideoItemContentProps> = ({
  label,
  data,
  itemWidth,
  isHovering = false,
  onThumbnailDisplayChange
}) => {
  const thumbnails = data?.thumbnails;
  const isLoadingThumbnails = data?.isLoadingThumbnails;
  const thumbnailError = data?.thumbnailError;

  const showLabelOnHover = false;

  // Track upload status for the video
  const [isUploading, setIsUploading] = useState(false);

  useEffect(() => {
    const checkUploadStatus = async () => {
      const src = data?.src || data?.originalUrl;
      if (!src) {
        setIsUploading(false);
        return;
      }

      // Only check blob URLs - Convex URLs are already uploaded
      if (!src.startsWith('blob:')) {
        setIsUploading(false);
        return;
      }

      try {
        const mediaItem = await findMediaBySrc(src);
        if (mediaItem) {
          const isPending =
            mediaItem.uploadStatus === 'pending' ||
            mediaItem.uploadStatus === 'uploading';
          setIsUploading(isPending);
        } else {
          setIsUploading(false);
        }
      } catch (error) {
        console.error('[VideoItemContent] Error checking upload status:', error);
        setIsUploading(false);
      }
    };

    checkUploadStatus();

    // Poll every 2 seconds
    const intervalId = setInterval(checkUploadStatus, 2000);
    return () => clearInterval(intervalId);
  }, [data?.src, data?.originalUrl]);

  // Determine if we're showing thumbnails
  const isShowingThumbnails = thumbnails && thumbnails.length > 0 && !isLoadingThumbnails;

  // Notify parent component when thumbnail display state changes
  React.useEffect(() => {
    onThumbnailDisplayChange?.(!!isShowingThumbnails);
  }, [isShowingThumbnails, onThumbnailDisplayChange]);

  // If we have thumbnails and enough width to display them meaningfully
  if (isShowingThumbnails) {
    const thumbnailWidth = itemWidth / thumbnails.length;

    return (
      <div className="flex items-center h-full w-full overflow-hidden relative rounded-[3px]">
        {/* Thumbnail strip */}
        <div className="flex-1 flex h-full">
          {thumbnails.map((thumbnail, index) => (
            <div
              key={index}
              className="shrink-0 h-full last:border-r-0 overflow-hidden relative"
              style={{ width: thumbnailWidth }}
            >
              <img
                src={thumbnail}
                alt={`Frame ${index + 1}`}
                className="absolute inset-0 w-full h-full object-cover opacity-90"
                style={{
                  imageRendering: 'crisp-edges'
                }}
              />
            </div>
          ))}
        </div>

        {/* Upload indicator badge */}
        {isUploading && (
          <div className="absolute top-1 right-1 bg-blue-500 text-white text-[10px] px-1.5 py-0.5 rounded flex items-center gap-1 z-10">
            <Upload className="w-2.5 h-2.5" />
            <span>Uploading</span>
          </div>
        )}

        {/* Overlay label when hovering or if item is very narrow */}
        {(isHovering || itemWidth < 80) && showLabelOnHover && ( // Reduced from 120 to 80px to better accommodate higher thumbnail density
          <div className="absolute inset-0 bg-white/10 backdrop-blur-xs">
            <TimelineItemLabel
              icon={Video}
              label={"VIDEO"}
              defaultLabel="VIDEO"
              isHovering={isHovering}
            />
          </div>
        )}
      </div>
    );
  }

  // Show loading state
  if (isLoadingThumbnails && (data?.src || data?.originalUrl)) {
    return (
      <div className="relative w-full h-full">
        <TimelineItemLabel
          icon={Loader2}
          label={"Loading..."}
          defaultLabel="VIDEO"
          iconClassName="w-4 h-4 animate-spin text-white/60"
          isHovering={isHovering}
        />
        {/* Upload indicator badge */}
        {isUploading && (
          <div className="absolute top-1 right-1 bg-blue-500 text-white text-[10px] px-1.5 py-0.5 rounded flex items-center gap-1 z-10">
            <Upload className="w-2.5 h-2.5" />
            <span>Uploading</span>
          </div>
        )}
      </div>
    );
  }

  // Show error state if thumbnail generation failed
  if (thumbnailError) {
    return (
      <div className="relative w-full h-full">
        <TimelineItemLabel
          icon={Video}
          label={"VIDEO"}
          defaultLabel="VIDEO"
          iconClassName="w-4 h-4 text-red-400"
          isHovering={isHovering}
        />
        {/* Upload indicator badge */}
        {isUploading && (
          <div className="absolute top-1 right-1 bg-blue-500 text-white text-[10px] px-1.5 py-0.5 rounded flex items-center gap-1 z-10">
            <Upload className="w-2.5 h-2.5" />
            <span>Uploading</span>
          </div>
        )}
      </div>
    );
  }

  // Fallback to simple label (no video source or small width)
  return (
    <div className="relative w-full h-full">
      <TimelineItemLabel
        icon={isUploading ? Upload : Video}
        label={isUploading ? "Uploading..." : label}
        defaultLabel="VIDEO"
        iconClassName={isUploading ? "w-4 h-4 text-blue-400 animate-pulse" : undefined}
        isHovering={isHovering}
      />
      {/* Upload indicator badge for when we have space */}
      {isUploading && itemWidth > 100 && (
        <div className="absolute top-1 right-1 bg-blue-500 text-white text-[10px] px-1.5 py-0.5 rounded flex items-center gap-1 z-10">
          <Upload className="w-2.5 h-2.5" />
          <span>Uploading</span>
        </div>
      )}
    </div>
  );
}; 